import { getInLocalStorage } from '@trqk/utils';

import {
    INSIGHTS_TOUR_CARD_IDS,
    INSIGHTS_TOUR_CARD_DONE,
} from '../../../../../constants';

import { getTourCardLocalStorageKey } from '../../../../../utils';

import {
    AddStatements,
    SetUpDetections,
    GapAnalysis,
    TrackPlacements,
    NegotiateDeals,
    InviteOthers,
} from './components';

interface GetSlideConfigsInput {
    detectionsActive: boolean;
    isPublisherEdition: Maybe<boolean>;
    statementHistoryData: InsightStatementHistoryData[] | null;
    entityId: string;
    userId: string;
}

export const getSlideConfigs = ({
    detectionsActive,
    isPublisherEdition,
    statementHistoryData,
    entityId,
    userId,
}: GetSlideConfigsInput): TourCardSlideConfig[] => {
    const localStorageKeys = {
        [INSIGHTS_TOUR_CARD_IDS.step3]: getTourCardLocalStorageKey({
            cardId: INSIGHTS_TOUR_CARD_IDS.step3,
            entityId,
            key: INSIGHTS_TOUR_CARD_DONE,
            userId,
        }),
        [INSIGHTS_TOUR_CARD_IDS.step4]: getTourCardLocalStorageKey({
            cardId: INSIGHTS_TOUR_CARD_IDS.step4,
            entityId,
            key: INSIGHTS_TOUR_CARD_DONE,
            userId,
        }),
        [INSIGHTS_TOUR_CARD_IDS.step6]: getTourCardLocalStorageKey({
            cardId: INSIGHTS_TOUR_CARD_IDS.step6,
            entityId,
            key: INSIGHTS_TOUR_CARD_DONE,
            userId,
        }),
    };

    if (isPublisherEdition) {
        // For Publisher Edition, remove the step about setting up detections because the user
        // cannot use the internal Trqk detection system; they are expected to have their own
        // relationship with a detection provider
        return [
            {
                id: INSIGHTS_TOUR_CARD_IDS.step1,
                Component: AddStatements,
                checked:
                    statementHistoryData !== null &&
                    statementHistoryData.length > 0,
                step: 1,
            },
            // leave these ids as-is (numbered off by 1) to maintain compatibility with:
            // packages/web/src/components/pages/Settings/components/InviteOthers/components/InvitationForm.tsx
            {
                id: INSIGHTS_TOUR_CARD_IDS.step3,
                Component: TrackPlacements,
                checked:
                    getInLocalStorage(
                        localStorageKeys[INSIGHTS_TOUR_CARD_IDS.step3],
                    ) ?? false,
                localStorageKey: localStorageKeys[INSIGHTS_TOUR_CARD_IDS.step3],
                step: 2,
            },
            {
                id: INSIGHTS_TOUR_CARD_IDS.step4,
                Component: NegotiateDeals,
                checked:
                    getInLocalStorage(
                        localStorageKeys[INSIGHTS_TOUR_CARD_IDS.step4],
                    ) ?? false,
                localStorageKey: localStorageKeys[INSIGHTS_TOUR_CARD_IDS.step4],
                step: 3,
            },
            {
                id: INSIGHTS_TOUR_CARD_IDS.step5,
                Component: GapAnalysis,
                checked: false,
                step: 4,
            },
            {
                id: INSIGHTS_TOUR_CARD_IDS.step6,
                Component: InviteOthers,
                checked:
                    getInLocalStorage(
                        localStorageKeys[INSIGHTS_TOUR_CARD_IDS.step6],
                    ) ?? false,
                localStorageKey: localStorageKeys[INSIGHTS_TOUR_CARD_IDS.step6],
                step: 5,
            },
        ];
    }

    // For Indie Edition, include the step about setting up Trqk detections and remove the step
    // about Gap Analysis because Gap Analysis is exclusively for Publisher Edition.
    return [
        {
            id: INSIGHTS_TOUR_CARD_IDS.step1,
            Component: AddStatements,
            checked:
                statementHistoryData !== null &&
                statementHistoryData.length > 0,
            step: 1,
        },
        {
            id: INSIGHTS_TOUR_CARD_IDS.step2,
            Component: SetUpDetections,
            checked: detectionsActive,
            step: 2,
        },
        {
            id: INSIGHTS_TOUR_CARD_IDS.step3,
            Component: TrackPlacements,
            checked:
                getInLocalStorage(
                    localStorageKeys[INSIGHTS_TOUR_CARD_IDS.step3],
                ) ?? false,
            localStorageKey: localStorageKeys[INSIGHTS_TOUR_CARD_IDS.step3],
            step: 3,
        },
        {
            id: INSIGHTS_TOUR_CARD_IDS.step4,
            Component: NegotiateDeals,
            checked:
                getInLocalStorage(
                    localStorageKeys[INSIGHTS_TOUR_CARD_IDS.step4],
                ) ?? false,
            localStorageKey: localStorageKeys[INSIGHTS_TOUR_CARD_IDS.step4],
            step: 4,
        },
        // leave this id as-is (numbered off by 1) to maintain compatibility with:
        // packages/web/src/components/pages/Settings/components/InviteOthers/components/InvitationForm.tsx
        {
            id: INSIGHTS_TOUR_CARD_IDS.step6,
            Component: InviteOthers,
            checked:
                getInLocalStorage(
                    localStorageKeys[INSIGHTS_TOUR_CARD_IDS.step6],
                ) ?? false,
            localStorageKey: localStorageKeys[INSIGHTS_TOUR_CARD_IDS.step6],
            step: 5,
        },
    ];
};
